import { useEffect, useState, useRef, useCallback } from "react";
import { FaTelegramPlane } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

interface RssItem {
  guid: string;
  link: string;
  pubDate: string;
  content: string;
}

const RSS_URL =
  "https://api.rss2json.com/v1/api.json?rss_url=https://rsshub.app/telegram/channel/nau_ucwaves";

/**
 * Floating Telegram live-feed indicator (mobile-first).
 *
 * - Polls the Telegram RSS every 30 s.
 * - When closed, shows:
 *   • Pastille rouge sur le bouton (compte d’« unread »).
 *   • Cercle rouge (toast) en haut à droite qui s’auto-masque après 3 s.
 * - Long hashes / adresses : `break-all` pour éviter le débordement.
 */
export default function TelegramLiveFeedWithNotify() {
  const [items, setItems] = useState<RssItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [showFeed, setShowFeed] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const lastGuidRef = useRef<string | null>(null);

  /* ------------------ Fetch + update ------------------ */
  const fetchFeed = useCallback(async () => {
    try {
      const res = await fetch(RSS_URL);
      const data = await res.json();
      const latest: RssItem | undefined = data.items?.[0];
      if (!latest) return;

      // compare with last stored guid
      if (
        lastGuidRef.current &&
        latest.guid !== lastGuidRef.current &&
        !showFeed
      ) {
        setUnread((u) => u + 1);
        setShowToast(true); // trigger toast
      }
      lastGuidRef.current =
        latest.guid || latest.link || latest.pubDate || null;
      setItems(data.items || []);
    } catch (e) {
      console.error("Telegram RSS fetch error:", e);
    }
  }, [showFeed]);

  /* ------------------- Polling ------------------------ */
  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 30_000);
    return () => clearInterval(id);
  }, [fetchFeed]);

  /* ---- Auto-dismiss toast after 3 s ------------------- */
  useEffect(() => {
    if (!showToast) return;
    const id = setTimeout(() => setShowToast(false), 3_000);
    return () => clearTimeout(id);
  }, [showToast]);

  /* ---- Close feed on outside click / blur ------------ */
  useEffect(() => {
    if (!showFeed) return;

    const handleClick = (e: MouseEvent) => {
      if (
        feedRef.current &&
        !feedRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setShowFeed(false);
      }
    };
    const handleBlur = () => setShowFeed(false);

    document.addEventListener("mousedown", handleClick);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("blur", handleBlur);
    };
  }, [showFeed]);

  /* ------------------- Handlers ----------------------- */
  const toggleFeed = () => {
    setShowFeed((p) => !p);
    setUnread(0);
    setShowToast(false);
  };

  /* -------------------- Render ------------------------ */
  return (
    <>
      {/* ===== Top-right toast ===== */}
      <AnimatePresence>
        {showToast && (
          <motion.span
            key="toast"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="fixed top-4 right-4 z-[10000] w-4 h-4 rounded-full bg-red-500"
          />
        )}
      </AnimatePresence>

      {/* ===== Floating Button ===== */}
      <button
        ref={buttonRef}
        onClick={toggleFeed}
        aria-label="Toggle Telegram feed"
        className="fixed bottom-4 left-4 z-[9999] flex items-center justify-center
                    w-14 h-14 rounded-full bg-blue-700 text-white shadow-xl
                    hover:scale-105 hover:bg-blue-800 transition
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <FaTelegramPlane className="text-2xl" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500
                        text-white text-xs font-bold flex items-center justify-center"
            aria-label={`${unread} unread messages`}
          >
            {unread}
          </span>
        )}
      </button>

      {/* ===== Feed pop-in ===== */}
      <AnimatePresence>
        {showFeed && (
          <motion.div
            ref={feedRef}
            key="feed"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="fixed bottom-20 left-1/2 -translate-x-1/2 md:left-4 md:translate-x-0
                        z-[9999] w-[90vw] md:w-96
                        bg-blue-300 rounded-xl shadow-lg border border-blue-400/45
                        p-4 md:p-6 max-h-[55vh] overflow-y-auto"
          >
            {items.length === 0 ? (
              <p className="text-center text-gray-600">Loading...</p>
            ) : (
              items.map((post) => (
                <article
                  key={post.guid}
                  className="relative bg-white border border-gray-200 rounded-2xl
                              p-4 shadow-sm mb-4 last:mb-0"
                >
                  {/* Bubble arrow */}
                  <span
                    className="absolute -left-2 top-4 w-0 h-0
                                border-t-8 border-b-8 border-r-8
                                border-t-transparent border-b-transparent border-r-white"
                  />
                  <p className="text-[11px] text-gray-500 mb-2">
                    {new Date(post.pubDate).toLocaleString()}
                  </p>
                  <div
                    className="prose prose-sm max-w-none text-gray-800 break-all
                                [&_a]:text-blue-700 [&_a:hover]:underline"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </article>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
